import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Loader2,
  ChevronsLeft,
  ChevronsRight,
  Menu,
  X,
  ChevronLeft,
  Gift,
  AlertCircle,
  ChevronsUpDown,
  Check,
  Plus,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { usePermissions } from "../context/PermissionContext";
import {
  showsV2Studio,
  showsClassicStudio,
  GRUPO_B,
} from "../lib/userGroups";
import { isUltra } from "../lib/planCapabilities";
import { MOBILE_BOTTOM_NAV_PATHS } from "./mobileNavConfig";
import { useSidebar } from "../context/SidebarContext";
import { supabase } from "../lib/supabase";
import { retrySupabase } from "../utils/retry";
import { mergeLastCrearAnuncioState } from "../utils/crearAnuncioStorage";
import { getUserPlan, planAllowsMultiBrand } from "../lib/planUtils";
import SubscriptionLimitModal from "./SubscriptionLimitModal";

// Sidebar aplanada: solo pestañas vigentes.
// Accesos en desuso comentados y se indica la categoría original.
// Categorías en desuso (antigua versión):
//  - Ventas (Registro de ventas, Historial de ventas, Mensajes, Clientes, Gráficos de Interacción, Envíos)
//  - Inventario (Lista de productos, Stock, Descuentos)
//  - Compras (Registro de compras, Historial de compras, Forecasting, Proveedores)
//  - Finanzas (Flujo de caja, Forecasting financiero, Gastos recurrentes)

type FlatMenuItem = {
  name: string;
  icon: React.ReactNode;
  path: string;
  permissionKey: string; // clave en PermissionContext
  // categoría original (solo para referencia, no visible)
  category: "Generación de imágenes" | "Empleado IA" | "Marca";
  devOnly?: boolean;
  // Si true, se muestra siempre (sin chequear PermissionContext). Para ítems
  // que no son features gateadas (ej. "Mi marca").
  alwaysShow?: boolean;
  // Experiencia de estudio a la que pertenece el ítem. Su visibilidad la decide
  // el grupo de testeo (ver lib/userGroups): "classic" → Grupo A + dev;
  // "v2" → Grupo B + dev. Los ítems sin `studioFlow` mantienen el gating normal
  // (permiso + devOnly).
  studioFlow?: "classic" | "v2";
};

// Pestañas vigentes (sin separación visible). Comentado en qué categoría estaban.
const FLAT_MENU_ITEMS: FlatMenuItem[] = [
  // Generación de imágenes
  {
    name: "Estudio Virtual",
    icon: <span className="material-symbols-rounded">image</span>,
    path: "/redes-sociales/crear-anuncio",
    permissionKey: "crear-anuncio",
    category: "Generación de imágenes",
    studioFlow: "classic",
  },
  {
    name: "Test V1",
    icon: <span className="material-symbols-rounded">smart_toy</span>,
    path: "/test-v1",
    permissionKey: "test-v1",
    category: "Generación de imágenes",
    devOnly: true,
  },
  {
    // Interfaz V2: flujo de generación nuevo (greeting + prompt bar +
    // colecciones de presets). Visible para Grupo B (etiquetado "Estudio
    // virtual", ver resolución de label más abajo) y para dev testers (que la
    // ven como "Interfaz V2"). El gating lo hace `studioFlow: "v2"`.
    name: "Interfaz V2",
    icon: <span className="material-symbols-rounded">auto_awesome</span>,
    path: "/interfaz-v2",
    permissionKey: "interfaz-v2",
    category: "Generación de imágenes",
    studioFlow: "v2",
  },
  {
    // "Modos": lista de modos de generación de V2 (la misma pantalla que el tab
    // de mobile). Gateada por `studioFlow: "v2"` → visible para Grupo B y dev
    // testers, idéntico a su tab en MobileBottomNav. Va justo después de
    // Interfaz V2, respetando el orden de mobile (Inicio → Modos → Historial).
    name: "Modos",
    icon: <span className="material-symbols-rounded">view_agenda</span>,
    path: "/modos",
    permissionKey: "modos",
    category: "Generación de imágenes",
    studioFlow: "v2",
  },
  {
    name: "Historial",
    icon: <span className="material-symbols-rounded">schedule</span>,
    path: "/redes-sociales/imagenes",
    permissionKey: "imagenes",
    category: "Generación de imágenes",
  },
  {
    name: "Estudio de Video",
    icon: <span className="material-symbols-rounded">movie_edit</span>,
    path: "/editor-video",
    permissionKey: "editor-video",
    category: "Generación de imágenes",
    devOnly: true,
  },
  // Empleado IA
  {
    name: "Manager de Redes IA",
    icon: <span className="material-symbols-rounded">smart_toy</span>,
    path: "/redes-sociales/calendario",
    permissionKey: "calendario",
    category: "Empleado IA",
    devOnly: true,
  },
  // Marca — visible para todos (no es una feature gateada).
  {
    name: "Mi marca",
    icon: <span className="material-symbols-rounded">shopping_bag</span>,
    path: "/mi-marca",
    permissionKey: "mi-marca",
    category: "Marca",
    alwaysShow: true,
  },
];

const PLAN_LABELS: Record<string, string> = {
  gratis: "Gratis",
  emprendimiento: "Básico",
  negocio: "Pro",
  enterprise: "Ultra",
  enterprise1: "Ultra",
  enterprise2: "Ultra",
  enterprise3: "Ultra",
  // Custom plan asignado a mano para clientes empresariales — comparte
  // todas las capabilities de los Ultra pero se etiqueta distinto en la UI.
  enterprise_plus: "Empresa",
};

interface UserProfile {
  id: string;
  name: string;
  email: string;
  id_company: string | null;
  company_name?: string | null;
  company_logo?: string | null;
  dev_tester?: boolean | null;
  grupo_testing?: string | null;
  plan?: string | null;
  role: string | null;
  max_fotos_mes?: number | null;
  imagenes_restantes?: number | null;
  pais?: string | null;
  fecha_cancelacion_programada?: string | null;
  fecha_reactivacion_programada?: string | null;
  /** Motivo de la cancelación. "fondos_insuficientes" → el último pago fue
   *  rechazado por falta de fondos (copy distinto en el banner). */
  trigger_cancelacion?: string | null;
}

// Menú hamburguesa de mobile: oculto por ahora. La navegación principal en
// mobile pasó a la barra inferior (MobileBottomNav); ajustes / cambio de marca
// / cerrar sesión se acceden vía "Mi marca" → Configuración. Poner en `true`
// para volver a mostrarlo.
const SHOW_MOBILE_MENU_BUTTON = false;

export const Sidebar = () => {
  const location = useLocation();
  const {
    isCollapsed,
    toggleCollapsed,
    mobileBackOverride,
    mobileHideBack,
    mobileHeaderTransparent,
    mobileHeaderCenter,
    mobileRightAction,
    mobileHideCreditsPill,
  } = useSidebar();

  // Animación de entrada/salida para `mobileHeaderCenter`: cuando el
  // contenido cambia entre null ↔ JSX, hacemos un fade + slide-up
  // sutil (200ms). Cambios in-place (mismo no-null → otro no-null,
  // ej. el toggle Imagen/Video cambiando su tab activo) se aplican
  // sin reanimar — `centerVisible` ya está true.
  const [displayedCenter, setDisplayedCenter] =
    useState<React.ReactNode | null>(mobileHeaderCenter);
  const [centerVisible, setCenterVisible] = useState<boolean>(
    !!mobileHeaderCenter,
  );
  useEffect(() => {
    if (mobileHeaderCenter) {
      setDisplayedCenter(mobileHeaderCenter);
      const id = requestAnimationFrame(() => setCenterVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setCenterVisible(false);
    const timer = setTimeout(() => setDisplayedCenter(null), 200);
    return () => clearTimeout(timer);
  }, [mobileHeaderCenter]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
    try {
      const cached = localStorage.getItem("atlas-user-plan");
      if (cached) return { plan: cached } as UserProfile;
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Brand switcher state
  type BrandRow = {
    id: string;
    name: string;
    logo_url: string | null;
    is_default: boolean;
  };
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [switchingBrand, setSwitchingBrand] = useState(false);
  const [showMultiBrandRequiredModal, setShowMultiBrandRequiredModal] = useState(false);

  const navigate = useNavigate();

  const activeBrand =
    brands.find((b) => b.id === currentBrandId) ??
    brands.find((b) => b.is_default) ??
    brands[0] ??
    null;

  // IMPORTANTE: si hay activeBrand, usar SOLO su logo. No caemos a userProfile.company_logo
  // porque eso muestra el logo de la marca default vieja en marcas adicionales sin logo.
  // Fallback a company_logo solo si no hay ninguna marca (legacy free tier sin onboarding).
  const displayLogo = activeBrand
    ? (activeBrand.logo_url || null)
    : (userProfile?.company_logo || null);
  const displayBrandName = activeBrand?.name || userProfile?.company_name || "";
  // OLD: mobileNavWidth and panelRef were for the slide-out panel — kept commented for reference
  // const [mobileNavWidth, setMobileNavWidth] = useState<number>(0);
  // const panelRef = useRef<HTMLDivElement | null>(null);

  // Clear Crear flow selected products when navigating via navbar/sidebar
  const clearCrearSelection = React.useCallback(() => {
    try {
      const isInCrearFlow = location.pathname.startsWith(
        "/redes-sociales/crear-anuncio",
      );
      if (isInCrearFlow) {
        mergeLastCrearAnuncioState({ selectedVariantIds: [] });
      }
    } catch {
      // noop
    }
  }, [location.pathname]);

  // Get user permissions
  const { permissions } = usePermissions();

  // Siempre remover espaciado lateral de sidebar (ya no se usa en desktop)
  useEffect(() => {
    document.documentElement.classList.add("no-sidebar-spacing");
    document.body.classList.add("no-sidebar-spacing");
    return () => {
      // mantener la clase para todo el app
      document.documentElement.classList.add("no-sidebar-spacing");
      document.body.classList.add("no-sidebar-spacing");
    };
  }, []);

  // OLD: Compute dynamic mobile nav width — no longer needed with dropdown menu
  // useEffect(() => { ... setMobileNavWidth(width); ... }, []);

  // Sync internal state when other components dispatch the same toggle event
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (typeof detail?.isMobileMenuOpen === "boolean") {
          setMobileMenuOpen(detail.isMobileMenuOpen);
        }
      } catch {
        // noop
      }
    };
    window.addEventListener(
      "sidebar-mobile-toggle" as any,
      handler as EventListener,
    );
    return () =>
      window.removeEventListener(
        "sidebar-mobile-toggle" as any,
        handler as EventListener,
      );
  }, []);

  // OLD: Apply/Remove inert on slide-out panel — no longer needed
  // useEffect(() => { panelRef.current... }, [mobileMenuOpen]);

  // Fetch user profile including company name
  const fetchUserProfile = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error } = await retrySupabase(
        async () =>
          await supabase
            .from("profiles")
            .select("id, name, email, id_company, role")
            .eq("id", user.id)
            .single(),
      );

      if (error) throw error;

      // If user has a company, fetch the company name and logo from company_profiles table
      if (profile.id_company) {
        const { data: companyData, error: companyError } = await retrySupabase(
          async () =>
            await supabase
              .from("company_profiles")
              .select(
                "nombre, logo_url, dev_tester, grupo_testing, plan, max_fotos_mes, imagenes_restantes, pais, fecha_cancelacion_programada, fecha_reactivacion_programada, trigger_cancelacion",
              )
              .eq("id_company", profile.id_company)
              .single(),
        );

        if (!companyError && companyData) {
          // Add the company name and logo to the profile object from company_profiles
          const profileWithCompanyInfo = {
            ...profile,
            company_name: companyData.nombre,
            company_logo: companyData.logo_url,
            dev_tester: companyData.dev_tester,
            grupo_testing: (companyData as any).grupo_testing,
            plan: companyData.plan,
            max_fotos_mes: companyData.max_fotos_mes,
            imagenes_restantes: companyData.imagenes_restantes,
            pais: companyData.pais,
            fecha_cancelacion_programada:
              companyData.fecha_cancelacion_programada,
            fecha_reactivacion_programada:
              (companyData as any).fecha_reactivacion_programada,
            trigger_cancelacion: (companyData as any).trigger_cancelacion,
          };
          setUserProfile(profileWithCompanyInfo);
          try {
            localStorage.setItem(
              "atlas-user-plan",
              companyData.plan || "gratis",
            );
          } catch {}
          return; // Exit early since we've already set the profile
        }
      }

      // If we get here, we either don't have a company or couldn't fetch company data
      setUserProfile(profile);
    } catch (err) {
      console.error("Error fetching user profile:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Cargar marcas y marca activa cuando hay id_company
  const fetchBrands = useCallback(async () => {
    if (!userProfile?.id_company) return;
    try {
      const { data: brandsData } = await supabase
        .from("brand_profiles")
        .select("id, name, logo_url, is_default")
        .eq("id_company", userProfile.id_company)
        .eq("is_draft", false)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });
      const list = (brandsData as BrandRow[] | null) ?? [];
      setBrands(list);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profData } = await supabase
        .from("profiles")
        .select("current_brand_id")
        .eq("id", user.id)
        .single();
      const fromProfile = (profData as any)?.current_brand_id as string | null;
      const fallback =
        list.find((b) => b.is_default)?.id ?? list[0]?.id ?? null;
      setCurrentBrandId(fromProfile ?? fallback);
    } catch {
      // Silencioso: si falla queda en modo legacy
    }
  }, [userProfile?.id_company]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  // Escuchar cambios de marca disparados desde otras pantallas
  useEffect(() => {
    const handler = () => {
      fetchBrands();
    };
    window.addEventListener("atlas:brand-changed", handler);
    return () => window.removeEventListener("atlas:brand-changed", handler);
  }, [fetchBrands]);

  const switchBrand = useCallback(
    async (brandId: string) => {
      if (brandId === currentBrandId || switchingBrand) {
        setShowBrandModal(false);
        return;
      }
      setSwitchingBrand(true);
      const prev = currentBrandId;
      setCurrentBrandId(brandId); // optimistic
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No user");
        const { error } = await supabase
          .from("profiles")
          .update({ current_brand_id: brandId })
          .eq("id", user.id);
        if (error) throw error;
        // Notificar al resto de la app
        window.dispatchEvent(
          new CustomEvent("atlas:brand-changed", {
            detail: { brandId },
          }),
        );
        setShowBrandModal(false);

        // Si el usuario estaba dentro del flujo de generación (crear-anuncio),
        // los productos/config seleccionados pertenecen a la marca anterior.
        // Llevarlo a Estudio Virtual landing para evitar inconsistencias.
        if (location.pathname.startsWith("/redes-sociales/crear-anuncio")) {
          // Limpiar selección persistida así no se arrastra entre marcas
          mergeLastCrearAnuncioState({ selectedVariantIds: [] });
          navigate("/redes-sociales/crear-anuncio/seleccionar-productos");
        }
      } catch {
        setCurrentBrandId(prev);
      } finally {
        setSwitchingBrand(false);
      }
    },
    [currentBrandId, switchingBrand, location.pathname, navigate],
  );

  // Listen for global credit-change events (e.g. after generating an image/video)
  // and silently refetch the remaining-images counter.
  // We refetch twice (immediate + delayed) to cover the case where Supabase
  // replica lag returns the stale value right after a generation decrement.
  useEffect(() => {
    const handler = () => {
      fetchUserProfile(true);
      // Retry after a short delay to catch replication lag on the DB read.
      setTimeout(() => {
        fetchUserProfile(true);
      }, 2000);
    };
    window.addEventListener("atlas:credits-updated", handler);
    return () => window.removeEventListener("atlas:credits-updated", handler);
  }, [fetchUserProfile]);

  // Se removió el manejo de secciones/categorías para aplanar la navegación

  // Toggle function removed; App.tsx controls mobile menu and dispatches the event.

  // Close mobile menu when a link is clicked
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    window.dispatchEvent(
      new CustomEvent("sidebar-mobile-toggle", {
        detail: { isMobileMenuOpen: false },
      }),
    );
  };

  // Mobile nav selection: clear Crear selection (if applicable) and close menu
  const handleMobileNavSelection = () => {
    clearCrearSelection();
    closeMobileMenu();
  };

  // Also close mobile menu when the route changes
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
      window.dispatchEvent(
        new CustomEvent("sidebar-mobile-toggle", {
          detail: { isMobileMenuOpen: false },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Items permitidos según permisos, dev_tester y grupo de testeo.
  const isDevTester = userProfile?.dev_tester === true;
  const grupo = userProfile?.grupo_testing ?? null;
  const isUltraPlan = isUltra(userProfile?.plan);
  const permittedFlatItems = FLAT_MENU_ITEMS.filter((item) => {
    if (item.alwaysShow) return true;
    // Estudio V2 (Interfaz V2 + Modos): gateado por grupo, no por permiso/devOnly.
    if (item.studioFlow === "v2") return showsV2Studio(isDevTester, grupo);
    if (!permissions[item.permissionKey]) return false;
    // Estudio clásico: además del permiso, solo para grupos clásicos (A) o dev.
    if (item.studioFlow === "classic")
      return showsClassicStudio(isDevTester, grupo);
    if (!item.devOnly) return true;
    if (isDevTester) return true;
    // El Estudio de Video se desbloquea para todos los planes Ultra
    // (enterprise/enterprise1/2/3 + el custom Empresa enterprise_plus), aunque
    // siga gateado `devOnly` para el resto. Otros ítems devOnly (Test V1,
    // Manager de Redes IA) quedan ocultos — son herramientas internas reales.
    if (item.permissionKey === "editor-video" && isUltraPlan) return true;
    return false;
  }).map((item) => {
    // Grupo B (no-dev) ve la Interfaz V2 etiquetada como "Estudio virtual".
    // Para dev testers el ítem sigue diciendo "Interfaz V2" (evita colisión con
    // el "Estudio Virtual" clásico que también ven).
    if (item.studioFlow === "v2" && item.path === "/interfaz-v2" && grupo === GRUPO_B && !isDevTester) {
      return { ...item, name: "Estudio virtual" };
    }
    return item;
  });

  // Toggle mobile menu locally (new dropdown style)
  const toggleMobileMenu = () => {
    const next = !mobileMenuOpen;
    setMobileMenuOpen(next);
    window.dispatchEvent(
      new CustomEvent("sidebar-mobile-toggle", {
        detail: { isMobileMenuOpen: next },
      }),
    );
  };

  // El header mobile ya no muestra título; `hideBackAndTitle` solo decide si
  // se oculta el back. Se oculta en las pantallas de entrada (selección de
  // productos) y en las "pantallas-tab" de la barra inferior (son raíces de
  // navegación: se cambian con las tabs, no con back). En `/interfaz-v2` se
  // respeta el back override del preset sheet (ahí sí debe haber back).
  const hideBackAndTitle =
    location.pathname === "/redes-sociales/crear-anuncio/seleccionar-productos" ||
    (MOBILE_BOTTOM_NAV_PATHS.includes(location.pathname) && !mobileBackOverride);
  // All pages now use Apple HIG style for the mobile header

  return (
    <>
      {/* ══════ Mobile Header (expands to show menu) ══════ */}
      {/* Estilo V2 unificado en TODAS las pantallas: fondo transparente y
          botones redondos blancos flotantes (back + menú). El header ya no
          muestra título ni logo — los títulos, donde hagan falta, viven
          dentro de cada página (left-aligned, arriba de todo). */}
      <header
        className="fixed top-0 left-0 right-0 md:hidden z-50 bg-transparent"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        {/* Chrome flotante: back (izq) | contenido centrado opcional | menú
            (der). Ya no hay título ni logo en el header — `hideBackAndTitle`
            ahora solo sirve para ocultar el back en pantallas de entrada
            (home V2, selección de productos). */}
        <div className="relative flex items-center h-14 px-4">
          {!mobileHideBack && !hideBackAndTitle && (
            <button
              onClick={() =>
                mobileBackOverride
                  ? mobileBackOverride()
                  : window.history.back()
              }
              className="inline-flex items-center justify-center shrink-0 active:opacity-50 transition-all w-12 h-12 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/85"
              aria-label="Volver"
            >
              <ChevronLeft size={26} />
            </button>
          )}
          {/* Contenido centrado opcional (ej. toggle Imagen/Video). NO es un
              título de página — eso vive dentro de cada página ahora. */}
          {displayedCenter && (
            <div
              className={`absolute left-1/2 -translate-x-1/2 transition-all duration-200 ease-out ${
                centerVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-2 pointer-events-none"
              }`}
            >
              {displayedCenter}
            </div>
          )}
          {/* Pill de créditos / imágenes restantes — sólo en el header
              transparente (home de Interfaz V2 y pantallas del preset
              sheet). Lleva al user a Configuración / billing al tappear.
              Toma `ml-auto` para empujar al hamburger contra el borde
              derecho; cuando este pill no se renderiza, el hamburger
              toma `ml-auto`. */}
          {(() => {
            const showCreditsPill =
              mobileHeaderTransparent &&
              !mobileHideCreditsPill &&
              !loading &&
              userProfile &&
              typeof userProfile.imagenes_restantes === "number";
            return (
              <>
                {showCreditsPill && (
                  <NavLink
                    to="/configuracion"
                    className={`ml-auto ${SHOW_MOBILE_MENU_BUTTON ? "mr-2" : ""} inline-flex items-center gap-1.5 h-12 px-3.5 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/85 text-[14.5px] font-semibold shrink-0`}
                    aria-label={`${userProfile!.imagenes_restantes} imágenes restantes`}
                  >
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="w-5 h-5 object-contain"
                    />
                    {userProfile!.imagenes_restantes}
                  </NavLink>
                )}
                {SHOW_MOBILE_MENU_BUTTON && (
                  <button
                    onClick={toggleMobileMenu}
                    className={`${showCreditsPill ? "" : "ml-auto"} inline-flex items-center justify-center transition-all shrink-0 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/70`}
                    aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
                  >
                    <Menu
                      size={24}
                      className={`transition-transform duration-300 ${mobileMenuOpen ? "rotate-90 opacity-0 absolute" : "rotate-0 opacity-100"}`}
                    />
                    <X
                      size={24}
                      className={`transition-transform duration-300 ${mobileMenuOpen ? "rotate-0 opacity-100" : "-rotate-90 opacity-0 absolute"}`}
                    />
                  </button>
                )}
              </>
            );
          })()}
          {/* Acción a la derecha inyectada por la página (ej. botón "Listo"
              de la grilla de resultados). Trae su propio `ml-auto`. */}
          {mobileRightAction}
        </div>
      </header>

      {/* ══════ Floating popover menu ══════ */}
      <div
        className={`fixed right-4 w-[272px] md:hidden z-[51] origin-top-right transition-all duration-200 ease-out ${
          mobileMenuOpen
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-[0.9] pointer-events-none"
        }`}
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 56px)" }}
      >
        <div className="bg-white rounded-[14px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] overflow-hidden">
          {/* ── Top section: credits bar, upgrade CTA, workspace card ── */}
          {!loading && userProfile && (
            <div className="px-3 pt-3 pb-2.5 flex flex-col gap-2.5">
              {/* Credits bar */}
              {(() => {
                const remaining =
                  typeof userProfile.imagenes_restantes === "number"
                    ? userProfile.imagenes_restantes
                    : 0;
                const max =
                  typeof userProfile.max_fotos_mes === "number"
                    ? userProfile.max_fotos_mes
                    : 0;
                const isFree = userProfile.plan === "gratis";
                const pct = isFree
                  ? max > 0
                    ? (remaining / max) * 100
                    : 0
                  : max > 0
                    ? (Math.min(remaining, max) / max) * 100
                    : 0;
                return (
                  <NavLink
                    to="/configuracion"
                    onClick={handleMobileNavSelection}
                    className="block px-1 py-0.5"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] text-brand-black/80">
                        {remaining}{" "}
                        {isFree
                          ? "fotos gratuitas"
                          : userProfile.plan === "enterprise_plus"
                            ? "créditos"
                            : "imágenes"}{" "}
                        restantes
                      </span>
                    </div>
                    <div className="h-[5px] bg-[#F2F2F7] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#1644EB] transition-all duration-300"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </NavLink>
                );
              })()}

              {/* Upgrade CTA (free plan only) */}
              {userProfile.plan === "gratis" && (
                <div className="flex items-center gap-2.5 bg-[#1644EB] rounded-[9px] px-3 h-[44px]">
                  <span className="material-symbols-rounded text-white text-[17px] shrink-0">
                    auto_awesome
                  </span>
                  <span className="text-[13px] text-white flex-1">
                    Mejorá tu plan
                  </span>
                  <NavLink
                    to="/subscription"
                    onClick={handleMobileNavSelection}
                    className="shrink-0 text-[13px] font-semibold text-[#1644EB] bg-white px-2.5 py-1 rounded-[6px] active:opacity-80 transition-opacity"
                  >
                    Ver planes
                  </NavLink>
                </div>
              )}

              {/* Workspace card — click abre modal de cambio de marca si hay >1 marca */}
              <div
                className="flex items-center gap-2.5 bg-[#F2F2F7] rounded-[9px] px-2.5 h-[44px] cursor-pointer active:opacity-80 transition-opacity"
                onClick={() => {
                  if (brands.length > 1) {
                    setShowBrandModal(true);
                  } else {
                    handleMobileNavSelection();
                    navigate("/configuracion");
                  }
                }}
              >
                <img
                  src={
                    displayLogo
                      ? (displayLogo.includes("supabase.co")
                          ? `${displayLogo}?width=32&height=32`
                          : displayLogo)
                      : "/images/estudio-atlas-logo-solo.svg"
                  }
                  alt="Logo"
                  className="w-8 h-8 rounded-[7px] object-contain bg-white shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/images/estudio-atlas-logo-solo.svg";
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-brand-black/90 truncate">
                    {userProfile.name || "Usuario"}
                  </div>
                  {displayBrandName && (
                    <div className="text-[11px] text-[#3C3C43]/60 truncate">
                      {displayBrandName}
                    </div>
                  )}
                </div>
                {brands.length > 1 ? (
                  <ChevronsUpDown className="w-4 h-4 text-neutral-400 shrink-0" />
                ) : userProfile.plan ? (
                  <span className="shrink-0 text-[11px] font-medium text-[#1644EB] bg-[#1644EB]/[0.08] px-1.5 py-0.5 rounded-full">
                    {PLAN_LABELS[userProfile.plan] || userProfile.plan}
                  </span>
                ) : null}
              </div>

              {/* Scheduled cancellation notice */}
              {userProfile.fecha_cancelacion_programada && (
                <div
                  className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-[9px] px-3 py-2.5 cursor-pointer active:opacity-80 transition-opacity"
                  onClick={() => {
                    handleMobileNavSelection();
                    navigate("/subscription");
                  }}
                >
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[13px] font-medium text-amber-800">
                      Cancelación programada
                    </p>
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      Tu plan se cancelará el{" "}
                      {new Date(
                        userProfile.fecha_cancelacion_programada,
                      ).toLocaleDateString("es-AR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                      {userProfile.trigger_cancelacion ===
                        "fondos_insuficientes" &&
                        " por rechazo de tu último pago."}
                    </p>
                  </div>
                </div>
              )}

              {/* Paused subscription notice */}
              {!userProfile.fecha_cancelacion_programada &&
                userProfile.fecha_reactivacion_programada &&
                new Date(userProfile.fecha_reactivacion_programada) >
                  new Date() && (
                  <div
                    className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-[9px] px-3 py-2.5 cursor-pointer active:opacity-80 transition-opacity"
                    onClick={() => {
                      handleMobileNavSelection();
                      navigate("/subscription");
                    }}
                  >
                    <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[13px] font-medium text-blue-800">
                        Suscripción pausada
                      </p>
                      <p className="text-[11px] text-blue-700 mt-0.5">
                        Se reanudará el{" "}
                        {new Date(
                          userProfile.fecha_reactivacion_programada,
                        ).toLocaleDateString("es-AR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* ── Main nav items ── */}
          <div className="px-1.5 pb-0.5">
            {permittedFlatItems.map((item: FlatMenuItem) => (
              <NavLink
                key={item.name}
                to={
                  item.path === "/redes-sociales/crear-anuncio"
                    ? "/redes-sociales/crear-anuncio/seleccionar-productos"
                    : item.path
                }
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                    isActive
                      ? "bg-[#1644EB]/[0.08] text-[#1644EB]"
                      : "text-brand-black/85 active:bg-black/[0.04]"
                  }`
                }
                onClick={handleMobileNavSelection}
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                        isActive
                          ? "bg-[#1644EB] text-white"
                          : "bg-[#F2F2F7] text-[#3C3C43]/70"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span>{item.name}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* ── Separator ── */}
          <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

          {/* ── Bottom section ── */}
          <div className="px-1.5 py-0.5">
            <NavLink
              to="/ayuda"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                  isActive
                    ? "bg-[#1644EB]/[0.08] text-[#1644EB]"
                    : "text-brand-black/85 active:bg-black/[0.04]"
                }`
              }
              onClick={handleMobileNavSelection}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                      isActive
                        ? "bg-[#1644EB] text-white"
                        : "bg-[#F2F2F7] text-[#3C3C43]/70"
                    }`}
                  >
                    <span className="material-symbols-rounded">help</span>
                  </span>
                  <span>Ayuda</span>
                </>
              )}
            </NavLink>
            <NavLink
              to="/configuracion"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                  isActive
                    ? "bg-[#1644EB]/[0.08] text-[#1644EB]"
                    : "text-brand-black/85 active:bg-black/[0.04]"
                }`
              }
              onClick={handleMobileNavSelection}
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] ${
                      isActive
                        ? "bg-[#1644EB] text-white"
                        : "bg-[#F2F2F7] text-[#3C3C43]/70"
                    }`}
                  >
                    <span className="material-symbols-rounded">settings</span>
                  </span>
                  <span>Configuración</span>
                </>
              )}
            </NavLink>
          </div>

          {/* ── Separator ── */}
          <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

          {/* ── Sign out ── */}
          <div className="px-1.5 py-0.5 pb-1.5">
            <button
              type="button"
              onClick={async () => {
                closeMobileMenu();
                await supabase.auth.signOut();
                navigate("/auth");
              }}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-brand-black/85 active:bg-black/[0.04] transition-colors w-full"
            >
              <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 [&>.material-symbols-rounded]:text-[14px] bg-[#F2F2F7] text-[#3C3C43]/70">
                <span className="material-symbols-rounded">logout</span>
              </span>
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </div>

      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 md:hidden z-40 transition-opacity duration-300 ${
          mobileMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* ══════ Desktop Sidebar ══════ */}
      <aside
        className="hidden md:flex fixed z-30 flex-col bg-[#F3F3F3] transition-[width] duration-300 ease-out"
        style={{ width: isCollapsed ? 72 : 264, top: 0, left: 0, bottom: 0 }}
      >
        {/* ── Logo + toggle de contraer (icono right-aligned a la altura del
            título; colapsado = el logo expande al tocarlo) ── */}
        <div
          className={`flex items-center h-16 shrink-0 ${isCollapsed ? "justify-center px-0" : "pl-6 pr-4"}`}
        >
          {isCollapsed ? (
            // Colapsado: en vez del logo, mostramos el botón para expandir
            // (espejo del ChevronsLeft de contraer), así queda claro que es
            // la acción de des-minimizar la sidebar.
            <button
              onClick={toggleCollapsed}
              title="Expandir"
              aria-label="Expandir menú"
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#2B2B2B]/55 hover:text-[#2B2B2B]/90 hover:bg-black/[0.04] transition-colors shrink-0"
            >
              <ChevronsRight size={18} />
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-sm bg-[#1644EB] flex items-center justify-center shrink-0">
                  <img
                    src="/images/estudio-atlas-logo-solo-blanco.svg"
                    alt=""
                    className="w-4 h-4"
                  />
                </div>
                <span className="text-[19px] leading-none text-[#2B2B2B] whitespace-nowrap">
                  <span className="font-display italic font-semibold">Estudio</span>
                  <span className="font-sans font-bold ml-0.5">Atlas</span>
                </span>
              </div>
              <button
                onClick={toggleCollapsed}
                title="Contraer"
                className="ml-auto -mr-3.5 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#2B2B2B]/55 hover:text-[#2B2B2B]/90 hover:bg-black/[0.04] transition-colors"
              >
                <ChevronsLeft size={18} />
              </button>
            </>
          )}
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 flex flex-col gap-0.5 px-4 pt-2 pb-3 overflow-y-auto">
          {permittedFlatItems.map((item: FlatMenuItem) => (
            <NavLink
              key={item.name}
              to={
                item.path === "/redes-sociales/crear-anuncio"
                  ? "/redes-sociales/crear-anuncio/seleccionar-productos"
                  : item.path
              }
              className={({ isActive }) =>
                `flex items-center gap-2.5 transition-colors ${isCollapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 h-9"} ${
                  isActive
                    ? "rounded-md bg-black/[0.06] text-[#2B2B2B] font-semibold nav-item-filled"
                    : "rounded-lg text-[#2B2B2B]/75 font-medium hover:bg-black/[0.04] hover:text-[#2B2B2B]/95 nav-item-outline"
                }`
              }
              title={item.name}
              onClick={clearCrearSelection}
            >
              <span className="shrink-0 inline-flex items-center [&>.material-symbols-rounded]:text-[18px]">
                {item.icon}
              </span>
              {!isCollapsed && (
                <span className="text-[13px] whitespace-nowrap overflow-hidden">
                  {item.name}
                </span>
              )}
            </NavLink>
          ))}

          {/* Divider */}
          <div
            className={`my-1.5 h-px bg-black/[0.07] ${isCollapsed ? "mx-1.5" : "mx-1"}`}
          />

          <NavLink
            to="/ayuda"
            className={({ isActive }) =>
              `flex items-center gap-2.5 transition-colors ${isCollapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 h-9"} ${
                isActive
                  ? "rounded-md bg-black/[0.06] text-[#2B2B2B] font-semibold nav-item-filled"
                  : "rounded-lg text-[#2B2B2B]/75 font-medium hover:bg-black/[0.04] hover:text-[#2B2B2B]/95 nav-item-outline"
              }`
            }
            title="Ayuda"
            onClick={clearCrearSelection}
          >
            <span className="material-symbols-rounded text-[18px] shrink-0">
              help
            </span>
            {!isCollapsed && (
              <span className="text-[13px] whitespace-nowrap overflow-hidden">
                Ayuda
              </span>
            )}
          </NavLink>
          <NavLink
            to="/configuracion"
            className={({ isActive }) =>
              `flex items-center gap-2.5 transition-colors ${isCollapsed ? "justify-center h-9 w-9 mx-auto" : "px-2.5 h-9"} ${
                isActive
                  ? "rounded-md bg-black/[0.06] text-[#2B2B2B] font-semibold nav-item-filled"
                  : "rounded-lg text-[#2B2B2B]/75 font-medium hover:bg-black/[0.04] hover:text-[#2B2B2B]/95 nav-item-outline"
              }`
            }
            title="Configuración"
            onClick={clearCrearSelection}
          >
            <span className="material-symbols-rounded text-[18px] shrink-0">
              settings
            </span>
            {!isCollapsed && (
              <span className="text-[13px] whitespace-nowrap overflow-hidden">
                Configuración
              </span>
            )}
          </NavLink>
        </nav>

        {/* ── Free trial upsell + User card at bottom ── */}
        <div className="shrink-0 px-4 pb-3">
          {!loading && userProfile?.plan === "gratis" && !isCollapsed && (
            <div
              className="w-full mb-2 bg-brand-primary rounded-md px-3 py-2.5 text-left cursor-pointer hover:bg-brand-primary-hover transition-colors"
              onClick={() => navigate("/subscription")}
            >
              <div className="flex items-start gap-2.5">
                <Gift className="w-6 h-6 text-white/80 mt-0.5 shrink-0" />
                <p className="text-[13px] text-white/90 leading-snug">
                  Te regalamos{" "}
                  <span className="font-bold">
                    {(userProfile?.pais || "").toLowerCase() === "argentina"
                      ? "30 imágenes gratis más."
                      : "30% de descuento en tu primer mes."}
                  </span>
                </p>
              </div>
              <span className="block mt-2 text-[11px] font-semibold text-brand-primary bg-white rounded px-2 py-1 text-center w-full">
                Reclamar
              </span>
            </div>
          )}

          {/* ── Imágenes restantes (arriba del container del perfil) ── */}
          {!loading &&
            userProfile &&
            !isCollapsed &&
            (() => {
              const remaining =
                typeof userProfile.imagenes_restantes === "number"
                  ? userProfile.imagenes_restantes
                  : 0;
              const max =
                typeof userProfile.max_fotos_mes === "number"
                  ? userProfile.max_fotos_mes
                  : 0;
              const isFree = userProfile.plan === "gratis";
              const pct = isFree
                ? max > 0
                  ? (remaining / max) * 100
                  : 0
                : max > 0
                  ? (Math.min(remaining, max) / max) * 100
                  : 0;
              return (
                <div
                  className="mb-3 px-1 cursor-pointer"
                  onClick={() => navigate("/configuracion")}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] text-brand-black/55">
                      {remaining}{" "}
                      {isFree
                        ? "fotos gratuitas"
                        : userProfile.plan === "enterprise_plus"
                          ? "créditos"
                          : "imágenes"}{" "}
                      restantes
                    </span>
                  </div>
                  <div className="h-[4px] bg-black/[0.08] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-primary transition-all duration-300"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })()}

          {loading ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-5 h-5 animate-spin text-brand-black/30" />
            </div>
          ) : userProfile ? (
            <div
              className={`cursor-pointer transition-colors ${isCollapsed ? "p-2 flex justify-center" : "px-1 py-2 flex items-center gap-3 rounded-md hover:bg-black/[0.03]"}`}
              onClick={() => {
                if (brands.length > 1) {
                  setShowBrandModal(true);
                } else {
                  navigate("/configuracion");
                }
              }}
            >
              <img
                src={
                  displayLogo
                    ? (displayLogo.includes("supabase.co")
                        ? `${displayLogo}?width=40&height=40`
                        : displayLogo)
                    : "/images/estudio-atlas-logo-solo.svg"
                }
                alt="Logo"
                className="w-8 h-8 rounded-full object-contain bg-white shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/images/estudio-atlas-logo-solo.svg";
                }}
              />
              {!isCollapsed && (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="text-[length:var(--text-body)] font-medium text-brand-black truncate">
                      {userProfile.name || "Usuario"}
                    </div>
                    {displayBrandName && (
                      <div className="text-[length:var(--text-caption)] text-[#2B2B2B]/60 truncate">
                        {displayBrandName}
                      </div>
                    )}
                  </div>
                  {brands.length > 1 ? (
                    <ChevronsUpDown className="w-4 h-4 text-brand-black/45 shrink-0" />
                  ) : userProfile.plan ? (
                    <span className="shrink-0 text-[11px] font-semibold leading-none px-1.5 py-0.5 rounded bg-black/[0.06] text-brand-black/65">
                      {PLAN_LABELS[userProfile.plan] || userProfile.plan}
                    </span>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {/* ── Scheduled cancellation notice (desktop) ── */}
          {!loading &&
            userProfile?.fecha_cancelacion_programada &&
            !isCollapsed && (
              <div
                className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => navigate("/subscription")}
              >
                <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-medium text-amber-700">
                    Cancelación programada
                  </p>
                  <p className="text-[11px] text-amber-600/80 mt-0.5">
                    Tu plan se cancelará el{" "}
                    {new Date(
                      userProfile.fecha_cancelacion_programada,
                    ).toLocaleDateString("es-AR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                    {userProfile.trigger_cancelacion ===
                      "fondos_insuficientes" &&
                      " por rechazo de tu último pago."}
                  </p>
                </div>
              </div>
            )}

          {/* ── Paused subscription notice (desktop) ── */}
          {!loading &&
            userProfile &&
            !userProfile.fecha_cancelacion_programada &&
            userProfile.fecha_reactivacion_programada &&
            new Date(userProfile.fecha_reactivacion_programada) >
              new Date() &&
            !isCollapsed && (
              <div
                className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => navigate("/subscription")}
              >
                <AlertCircle className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-medium text-blue-700">
                    Suscripción pausada
                  </p>
                  <p className="text-[11px] text-blue-600/80 mt-0.5">
                    Se reanudará el{" "}
                    {new Date(
                      userProfile.fecha_reactivacion_programada,
                    ).toLocaleDateString("es-AR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            )}
        </div>
      </aside>

      {/* ══════ Brand switcher modal ══════ */}
      {showBrandModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={() => !switchingBrand && setShowBrandModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                Cambiar de marca
              </h3>
              <button
                onClick={() => setShowBrandModal(false)}
                disabled={switchingBrand}
                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors disabled:opacity-50"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto scrollbar-thin">
              {brands.map((b) => {
                const isActive = b.id === currentBrandId;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => switchBrand(b.id)}
                    disabled={switchingBrand}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-neutral-50 transition-colors disabled:opacity-60"
                  >
                    <img
                      src={
                        b.logo_url
                          ? (b.logo_url.includes("supabase.co")
                              ? `${b.logo_url}?width=40&height=40`
                              : b.logo_url)
                          : "/images/estudio-atlas-logo-solo.svg"
                      }
                      alt=""
                      className="w-9 h-9 rounded-[7px] object-contain bg-white border border-neutral-200 shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/images/estudio-atlas-logo-solo.svg";
                      }}
                    />
                    <span className="flex-1 text-[14px] font-medium text-brand-black/90 truncate">
                      {b.name}
                    </span>
                    {isActive && (
                      <Check className="w-4 h-4 text-brand-primary shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="border-t border-neutral-200">
              <button
                type="button"
                onClick={async () => {
                  const plan = await getUserPlan();
                  if (!planAllowsMultiBrand(plan)) {
                    setShowBrandModal(false);
                    setShowMultiBrandRequiredModal(true);
                    return;
                  }
                  setShowBrandModal(false);
                  navigate(
                    "/redes-sociales/descripcion-brand?mode=additional",
                  );
                }}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-neutral-50 transition-colors text-brand-primary"
              >
                <div className="w-9 h-9 rounded-[7px] border border-dashed border-brand-primary/40 flex items-center justify-center shrink-0">
                  <Plus className="w-4 h-4" />
                </div>
                <span className="text-[14px] font-medium">
                  Añadir nueva marca
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionLimitModal
        isOpen={showMultiBrandRequiredModal}
        onClose={() => setShowMultiBrandRequiredModal(false)}
        type="multi-brand-required"
      />
    </>
  );
};
